import { useEffect, useMemo, useRef, useState } from 'react'
import type { ChangeEvent } from 'react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { Search, Download, ArrowUpDown } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { TransactionList } from '@/components/transactionspage'
import { useInfiniteQuery } from 'react-query'
import { reportsApi, transactionApi } from '@/services/api'
import type { Transaction } from '@/types'

export function TransactionsPage() {
  const { t } = useTranslation()
  const [searchTerm, setSearchTerm] = useState('')
  const [filterType, setFilterType] = useState('all')
  const [isExportOpen, setIsExportOpen] = useState(false)
  const [exportPeriod, setExportPeriod] = useState('month')
  const [exportMonth, setExportMonth] = useState('')
  const [isExporting, setIsExporting] = useState(false)
  const loadMoreRef = useRef<HTMLDivElement | null>(null)

  const normalizedSearch = useMemo(() => searchTerm.trim(), [searchTerm])
  const apiFilterType = useMemo(() => {
    switch (filterType) {
      case 'payment':
        return 'MERCHANT'
      case 'transfer':
        return 'P2P'
      case 'deposit':
        return 'CASH_IN'
      case 'withdrawal':
        return 'CASH_OUT'
      default:
        return undefined
    }
  }, [filterType])

  const { data, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage } = useInfiniteQuery(
    ['transactions', { search: normalizedSearch, type: apiFilterType }],
    async ({ pageParam = 1 }) => {
      try {
        const response = await transactionApi.getTransactions({
          page: pageParam,
          page_size: 20,
          limit: 20,
          type: apiFilterType,
          search: normalizedSearch || undefined,
        })

        const rawData = response.data as any
        let results: Transaction[] = []
        let nextPage: number | undefined

        if (rawData?.results && Array.isArray(rawData.results)) {
          results = rawData.results
          if (rawData.next) {
            const nextUrl = new URL(rawData.next, window.location.origin)
            const nextParam = nextUrl.searchParams.get('page')
            nextPage = nextParam ? Number(nextParam) : pageParam + 1
          }
        } else if (rawData?.data?.results && Array.isArray(rawData.data.results)) {
          results = rawData.data.results
        } else if (rawData?.data && Array.isArray(rawData.data)) {
          results = rawData.data
        } else if (Array.isArray(rawData)) {
          results = rawData
        }

        return { results, nextPage }
      } catch (error) {
        console.error('Error fetching transactions:', error)
        return { results: [], nextPage: undefined }
      }
    },
    {
      getNextPageParam: (lastPage) => lastPage.nextPage,
      refetchInterval: 10000,
    }
  )

  const transactions = data?.pages.flatMap((page) => page.results) ?? []

  useEffect(() => {
    const target = loadMoreRef.current
    if (!target) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage()
        }
      },
      { rootMargin: '200px' }
    )
    observer.observe(target)
    return () => observer.disconnect()
  }, [fetchNextPage, hasNextPage, isFetchingNextPage])

  const filterTypes = [
    { id: 'all', label: t('transactions.all') },
    { id: 'payment', label: t('transactions.payment') },
    { id: 'transfer', label: t('transactions.transfer') },
    { id: 'deposit', label: t('transactions.deposit') },
    { id: 'withdrawal', label: t('transactions.withdrawal') }
  ]

  const handleExport = async () => {
    setIsExporting(true)
    try {
      const params: Parameters<typeof reportsApi.exportReport>[0] = {
        format: 'csv',
        period: exportPeriod === 'specific_month' ? 'month' : exportPeriod,
      }
      if (exportPeriod === 'specific_month' && exportMonth) {
        const [year, month] = exportMonth.split('-')
        params.month = Number(month)
        params.year = Number(year)
      }
      await reportsApi.exportReport(params)
      setIsExportOpen(false)
    } catch (error) {
      console.error('Error exporting transactions:', error)
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <div className="min-h-screen bg-background-light">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 p-4">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-bold text-text-primary">{t('navigation.transactions')}</h1>
          <Button variant="outline" size="sm" onClick={() => setIsExportOpen(true)}>
            <Download className="h-4 w-4 mr-2" />
            {t('common.export')}
          </Button>
        </div>

        {/* Search and Filters */}
        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-text-secondary" />
            <Input
              type="text"
              placeholder={t('transactions.searchTransactions')}
              value={searchTerm}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          <div className="flex items-center space-x-2 overflow-x-auto">
            {filterTypes.map((type) => (
              <Button
                key={type.id}
                variant={filterType === type.id ? 'primary' : 'outline'}
                size="sm"
                onClick={() => setFilterType(type.id)}
                className="whitespace-nowrap"
              >
                {type.label}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* Transactions List */}
      <div className="p-4">
        {isLoading ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="bg-white rounded-xl p-4 animate-pulse">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gray-200 rounded-full" />
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded mb-2" />
                    <div className="h-3 bg-gray-200 rounded w-2/3" />
                  </div>
                  <div className="h-4 bg-gray-200 rounded w-16" />
                </div>
              </div>
            ))}
          </div>
        ) : transactions && transactions.length > 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-3"
          >
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <TransactionList transactions={transactions} showBalance={true} />
            </div>
            <div ref={loadMoreRef} className="h-8" />
            {isFetchingNextPage && (
              <div className="text-center text-sm text-text-secondary">
                {t('transactions.loadingMore')}
              </div>
            )}
          </motion.div>
        ) : (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <ArrowUpDown className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-text-primary mb-2">
              {t('transactions.noTransactions')}
            </h3>
            <p className="text-text-secondary">
              {searchTerm || filterType !== 'all' 
                ? t('transactions.adjustFilters')
                : t('transactions.transactionsWillAppear')
              }
            </p>
          </div>
        )}
      </div>

      {isExportOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <h2 className="text-lg font-semibold text-text-primary">
              {t('transactions.exportTitle')}
            </h2>
            <p className="mt-1 text-sm text-text-secondary">
              {t('transactions.exportDescription')}
            </p>

            <div className="mt-4">
              <label className="text-sm font-medium text-text-primary">
                {t('transactions.exportPeriodLabel')}
              </label>
              <select
                value={exportPeriod}
                onChange={(event) => setExportPeriod(event.target.value)}
                className="mt-2 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/40"
              >
                <option value="week">{t('transactions.exportPeriodWeek')}</option>
                <option value="month">{t('transactions.exportPeriodMonth')}</option>
                <option value="year">{t('transactions.exportPeriodYear')}</option>
                <option value="specific_month">{t('transactions.exportPeriodSpecificMonth')}</option>
              </select>
            </div>

            {exportPeriod === 'specific_month' && (
              <div className="mt-4">
                <label className="text-sm font-medium text-text-primary">
                  {t('transactions.exportMonthLabel')}
                </label>
                <input
                  type="month"
                  value={exportMonth}
                  onChange={(event) => setExportMonth(event.target.value)}
                  className="mt-2 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/40"
                />
              </div>
            )}

            <div className="mt-6 flex gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsExportOpen(false)}
                disabled={isExporting}
                className="flex-1"
              >
                {t('transactions.exportCancel')}
              </Button>
              <Button
                size="sm"
                onClick={handleExport}
                loading={isExporting}
                disabled={exportPeriod === 'specific_month' && !exportMonth}
                className="flex-1"
              >
                {isExporting ? t('transactions.exportLoading') : t('transactions.exportConfirm')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
